import React, { useState, useEffect } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControlLabel,
  Paper,
  Step,
  StepLabel,
  Stepper,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import SaveIcon from '@mui/icons-material/Save'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import useRouter from '../hooks/useRouter'
import useAccount from '../hooks/useAccount'
import useFrameworkLibrary from '../hooks/useFrameworkLibrary'
import ManualFrameworkEditor from '../components/autocoder/engine/ManualFrameworkEditor'
import { AutoCoderFramework } from '../types/gotypes'
import { FrameworkData } from '../hooks/useFrameworkLibrary'
import PageAgentButton from '../agent/components/PageAgentButton'
import PageAgentDrawer from '../agent/components/PageAgentDrawer'
import { useVoiceRegistration } from '../voice/useVoiceRegistration'
import { buildVoiceTools } from '../voice/buildVoiceTools'
import { useDisplayNames } from '../utils/displayNames'

const FrameworkEditor: React.FC = () => {
  const router = useRouter()
  const account = useAccount()

  const customer = router.params.customer || account.customer?.name || ''
  const job = router.params.job || account.job?.name || ''
  const frameworkId = router.params.frameworkId || ''

  const isManualMode = frameworkId === 'new-manual'
  const isThemeAIMode = frameworkId === 'new-themeai'
  const isEditMode = !isManualMode && !isThemeAIMode

  const {
    framework,
    frameworkLoading,
    frameworkError,
    createFramework,
    updateFramework,
    createLoading,
    updateLoading,
    setFrameworkDynamic,
    dynamicLoading,
  } = useFrameworkLibrary({ customer, job, frameworkId })

  // Form state
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [frameworks, setFrameworks] = useState<AutoCoderFramework[]>([{
    name: '',
    main_variable: { name: 'topic', codes: [] },
    suggestions: [],
    looped_variables: [],
  }])

  // Stepper state for manual/themeai modes
  const [activeStep, setActiveStep] = useState(0)
  // The page helper (Douglas plan T16).
  const [helperOpen, setHelperOpen] = useState(false)

  // Load existing framework data
  useEffect(() => {
    if (isEditMode && framework) {
      setName(framework.name || '')
      setDescription(framework.description || '')
      if (framework.data) {
        setFrameworks([{
          name: framework.data.name || name || '',
          main_variable: framework.data.main_variable || { name: 'topic', codes: [] },
          suggestions: framework.data.suggestions || [],
          looped_variables: framework.data.looped_variables || [],
        }])
      }
    }
  }, [isEditMode, framework])

  const handleBack = () => {
    router.navigate('frameworks', { customer, job })
  }

  const handleSave = async () => {
    if (!name.trim()) {
      return
    }

    const currentFramework = frameworks[0]
    const data: FrameworkData = {
      name: name,
      main_variable: currentFramework.main_variable,
      suggestions: currentFramework.suggestions || [],
      looped_variables: [],
    }

    if (isEditMode && frameworkId) {
      await updateFramework(frameworkId, {
        name,
        description,
        data,
      })
      router.navigate('frameworks', { customer, job })
    } else {
      await createFramework({
        name,
        description,
        data,
      })
      router.navigate('frameworks', { customer, job })
    }
  }

  // Douglas reads this back aloud, so it carries display names rather than

  // the codes in the URL (#1347 item 1).

  const names = useDisplayNames(customer)


  useVoiceRegistration({
    pageKey: `framework-editor:${customer}:${job}${frameworkId ? `:${frameworkId}` : ''}`,
    pageContext: () =>
      `Code Frame Editor for customer ${names.customer}, job ${names.job(job)}` +
      (isEditMode && framework ? `, editing "${framework.name || frameworkId}"` : ', creating a new code frame') +
      `. Current step: ${activeStep === 0 ? 'framework info' : 'define codes'}.` +
      ` Available: set name, set description, save, navigate between steps.`,
    uiTools: buildVoiceTools({
      fields: [
        {
          name: 'set_framework_name',
          label: 'the code frame name',
          set: (v) => setName(v),
        },
        {
          name: 'set_framework_description',
          label: 'the code frame description',
          set: (v) => setDescription(v),
        },
      ],
      actions: [
        {
          name: 'save_code_frame',
          label: 'save the code frame',
          run: () => handleSave(),
        },
        {
          name: 'go_to_define_codes_step',
          label: 'go to the define-codes step',
          run: () => setActiveStep(1),
        },
        {
          name: 'go_to_framework_info_step',
          label: 'go back to the framework info step',
          run: () => setActiveStep(0),
        },
      ],
    }),
  })

  const manualSteps = ['Code Frame Info', 'Define Codes']
  const themeaiSteps = ['Code Frame Info', 'Select Data Source', 'Extract Themes']

  const canSave = name.trim().length > 0 && frameworks[0]?.main_variable?.codes?.length > 0

  if (frameworkLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (frameworkError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {frameworkError instanceof Error ? frameworkError.message : 'Failed to load framework'}
        </Alert>
      </Box>
    )
  }

  // ThemeAI mode placeholder
  if (isThemeAIMode) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: 'calc(100vh - 84px)', p: 3 }}>
        <Paper elevation={1} sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
            >
              Back
            </Button>
            <Typography variant='h5'>New Code Frame from ThemeAI</Typography>
          </Box>
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {themeaiSteps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          <Alert severity="info">
            ThemeAI framework extraction is coming soon. For now, please use the Manual Editor to create frameworks,
            or extract frameworks from existing ThemeAI pipelines using the "Extract Framework" option in the pipeline menu.
          </Alert>
        </Paper>
      </Box>
    )
  }

  // Manual mode or Edit mode
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: 'calc(100vh - 84px)', p: 3 }}>
      <Paper elevation={1} sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
            >
              Back
            </Button>
            <Typography variant='h5'>
              {isEditMode ? 'Edit Code Frame' : 'New Code Frame'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PageAgentButton
              pageKey="framework-editor"
              customer={account.customer?.name || customer}
              open={helperOpen}
              onClick={() => setHelperOpen(o => !o)}
            />
          <Button
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={!canSave || createLoading || updateLoading}
          >
            {createLoading || updateLoading ? 'Saving...' : 'Save Code Frame'}
          </Button>
          </Box>
        </Box>

        {isManualMode && (
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {manualSteps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        )}
      </Paper>

      {/* Edit mode: Single scrollable Paper with both sections */}
      {isEditMode && (
        <Paper elevation={1} sx={{ p: 3, flex: 1, overflow: 'auto' }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Framework Information</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Framework Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              fullWidth
              placeholder="e.g., Customer Feedback Themes"
            />
            <TextField
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              multiline
              rows={2}
              fullWidth
              placeholder="Optional description of this framework"
            />
            {/* Dynamic code frames (docs/autocoder-dynamic-frames.md): the switch saves at once. */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(framework?.dynamic)}
                    disabled={dynamicLoading || !framework}
                    onChange={(_, checked) => framework && setFrameworkDynamic(framework.id, checked)}
                    inputProps={{ 'aria-label': 'Dynamic' }}
                  />
                }
                label="Dynamic"
              />
              <Typography variant="caption" color="text.secondary" sx={{ flex: 1, minWidth: 240 }}>
                Collects the model's suggestions for new codes while comments are coded. The frame changes only when you accept one, and codes are only ever added at the end.
              </Typography>
              {framework?.dynamic && (
                <Button
                  variant="outlined"
                  color="info"
                  size="small"
                  onClick={() => router.navigate('framework-suggestions', { customer, job, frameworkId })}
                >
                  Review suggestions
                </Button>
              )}
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" sx={{ mb: 2 }}>Coding Framework</Typography>
          <ManualFrameworkEditor
            frameworks={frameworks}
            onChange={setFrameworks}
          />
        </Paper>
      )}

      {/* Manual mode Step 0: Framework Info */}
      {isManualMode && activeStep === 0 && (
        <Paper elevation={1} sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Framework Information</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Framework Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              fullWidth
              placeholder="e.g., Customer Feedback Themes"
            />
            <TextField
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              multiline
              rows={2}
              fullWidth
              placeholder="Optional description of this framework"
            />
          </Box>
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              onClick={() => setActiveStep(1)}
              disabled={!name.trim()}
            >
              Next: Define Codes
            </Button>
          </Box>
        </Paper>
      )}

      {/* Manual mode Step 1: Define Codes */}
      {isManualMode && activeStep === 1 && (
        <Paper elevation={1} sx={{ p: 3, flex: 1, overflow: 'auto' }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Coding Framework</Typography>
          <ManualFrameworkEditor
            frameworks={frameworks}
            onChange={setFrameworks}
          />
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
            <Button
              onClick={() => setActiveStep(0)}
            >
              Back to Info
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={!canSave || createLoading || updateLoading}
            >
              {createLoading || updateLoading ? 'Saving...' : 'Save Code Frame'}
            </Button>
          </Box>
        </Paper>
      )}

      <PageAgentDrawer
        pageKey="framework-editor"
        customer={account.customer?.name || customer}
        job={job}
        open={helperOpen}
        onClose={() => setHelperOpen(false)}
      />
    </Box>
  )
}

export default FrameworkEditor
